<script module>
  // Each instance needs a stable id to wire the input's aria-controls/aria-activedescendant to
  // its listbox. ($props.id() would do this, but it landed after the Svelte version we pin.)
  let instances = 0;
</script>

<script>
  // Type-to-filter dropdown, replacing the native <select> the Svelte port used.
  //
  // The React app got this for free from react-select via the shared
  // src/user/modules/ui_elements/select_inline.jsx — `isSearchable` defaults to true, so every
  // dropdown built on it could be filtered by typing. The port to native <select> silently
  // dropped that: a client with a long list of saved withdrawal wallets had to scroll it by hand.
  //
  // Controlled like a native <select> — `bind:value` plus an optional `onchange`, which fires
  // only on a real user selection, never on a programmatic change to `value`.
  import { t } from "../i18n/i18n.svelte.js";

  let {
    options = [],
    value = $bindable(""),
    placeholder = "",
    disabled = false,
    id = undefined,
    ariaLabel = undefined,
    onchange,
  } = $props();

  const listId = `searchable-select-${++instances}`;

  let open = $state(false);
  let query = $state("");
  let activeIndex = $state(0);
  let listElement = $state(null);
  let inputElement = $state(null);
  // Inline because the menu is positioned against the viewport — see positionMenu.
  let menuStyle = $state("");

  const MaxMenuHeight = 240;
  // Below this much room, dropping downward would leave a uselessly short menu; flip up instead.
  const MinMenuHeight = 120;

  const selectedLabel = $derived(options.find((option) => option.value === value)?.label ?? "");
  const filtered = $derived.by(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => String(option.label).toLowerCase().includes(needle));
  });

  // While the menu is open the box holds the live query and shows the current selection as its
  // placeholder — react-select's behaviour, so typing never destroys the visible selection.
  const displayValue = $derived(open ? query : selectedLabel);

  // The menu is `position: fixed` and measured against the input rather than laid out beneath it,
  // so it escapes every ancestor's overflow. It has to: this control is used inside Modal.svelte,
  // whose `.modal-body { overflow-y: auto }` would otherwise clip the list to the dialog and make a
  // long one scroll within a scroll. Fixed also means it paints OVER the fields below it instead of
  // pushing them down. No ancestor sets transform/filter/perspective, so fixed stays viewport-based.
  function positionMenu() {
    if (!inputElement) return;
    const rect = inputElement.getBoundingClientRect();
    const roomBelow = window.innerHeight - rect.bottom;
    const roomAbove = rect.top;
    const flip = roomBelow < MinMenuHeight && roomAbove > roomBelow;
    const height = Math.min(MaxMenuHeight, (flip ? roomAbove : roomBelow) - 12);
    const box = `left:${rect.left}px; width:${rect.width}px; max-height:${height}px;`;
    menuStyle = flip
      ? `${box} bottom:${window.innerHeight - rect.top + 2}px;`
      : `${box} top:${rect.bottom + 2}px;`;
  }

  function openMenu() {
    if (disabled || open) return;
    open = true;
    query = "";
    activeIndex = Math.max(
      0,
      options.findIndex((option) => option.value === value),
    );
  }

  function closeMenu() {
    open = false;
    query = "";
  }

  function choose(option) {
    if (!option) return;
    value = option.value;
    closeMenu();
    onchange?.(option.value);
  }

  function onKeyDown(event) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        if (!open) return openMenu();
        if (filtered.length) {
          const step = event.key === "ArrowDown" ? 1 : -1;
          activeIndex = (activeIndex + step + filtered.length) % filtered.length;
        }
        break;
      case "Enter":
        // Only swallow Enter while the menu is open, so it still submits the surrounding form.
        if (open) {
          event.preventDefault();
          choose(filtered[activeIndex]);
        }
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          closeMenu();
        }
        break;
      case "Tab":
        closeMenu();
        break;
    }
  }

  function onInput(event) {
    query = event.currentTarget.value;
    open = true;
    activeIndex = 0;
  }

  // Closes on click-outside and on tabbing away. focusout fires before the click on an option,
  // hence the preventDefault on each option's mousedown below — it keeps focus on the input so
  // the menu is still mounted when the click lands.
  function onFocusOut(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) closeMenu();
  }

  $effect(() => {
    if (!open || !listElement) return;
    // Optional call, as openPicker.js does for showPicker: jsdom implements neither.
    listElement.querySelector("[data-active='true']")?.scrollIntoView?.({ block: "nearest" });
  });

  // A viewport-positioned menu has to be re-measured whenever anything moves under it. Capture
  // phase, because the scroll that matters is usually the modal body's, not the window's.
  $effect(() => {
    if (!open) return;
    positionMenu();
    const reposition = () => positionMenu();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  });
</script>

<div class="searchable-select" class:open onfocusout={onFocusOut}>
  <input
    {id}
    {disabled}
    bind:this={inputElement}
    type="text"
    role="combobox"
    autocomplete="off"
    aria-expanded={open}
    aria-controls={listId}
    aria-autocomplete="list"
    aria-label={ariaLabel}
    aria-activedescendant={open && filtered.length ? `${listId}-${activeIndex}` : undefined}
    value={displayValue}
    placeholder={open && selectedLabel ? selectedLabel : placeholder}
    onclick={openMenu}
    oninput={onInput}
    onkeydown={onKeyDown}
  />
  <span class="arrow" aria-hidden="true"></span>

  {#if open}
    <ul class="menu" id={listId} role="listbox" style={menuStyle} bind:this={listElement}>
      {#each filtered as option, index (option.value)}
        <!-- The options carry no key handler by design. This is the ARIA combobox pattern: focus
             stays on the input, which owns every key (arrows/Enter/Escape) and points at the
             highlighted option via aria-activedescendant. Making each <li> focusable instead
             would take focus off the input and break the typing that is the point of this
             component. -->
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <li
          id={`${listId}-${index}`}
          role="option"
          aria-selected={option.value === value}
          data-active={index === activeIndex}
          class:active={index === activeIndex}
          onmousedown={(event) => event.preventDefault()}
          onclick={() => choose(option)}
          onmouseenter={() => (activeIndex = index)}
        >
          {option.label}
        </li>
      {/each}
      {#if !filtered.length}
        <li class="empty">{t("no_options")}</li>
      {/if}
    </ul>
  {/if}
</div>

<style>
  .searchable-select {
    position: relative;
    width: 100%;
  }
  /* The box itself is deliberately unstyled beyond this: app.css styles input[type="text"]
     globally, so it already matches every other field. min-width overrides the 150px floor from
     that rule, which a scoped `.filters input { min-width: 0 }` cannot reach into a component. */
  .searchable-select input {
    width: 100%;
    min-width: 0;
    /* Padded right so a long label never runs under the arrow. */
    padding-right: 28px;
    cursor: pointer;
    text-overflow: ellipsis;
  }
  .searchable-select.open input {
    cursor: text;
  }
  .arrow {
    position: absolute;
    top: 50%;
    right: 10px;
    width: 0;
    height: 0;
    margin-top: -2px;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid var(--muted);
    pointer-events: none;
  }
  .menu {
    /* Fixed, with left/top/width/max-height supplied inline by positionMenu. z-index clears
       Modal.svelte's backdrop (100) so the list paints over the dialog, not inside it. */
    position: fixed;
    z-index: 200;
    overflow-y: auto;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: var(--card-bg);
    border: 1px solid var(--border);
    border-radius: 7px;
    box-shadow: var(--shadow);
    font-size: 13px;
  }
  .menu li {
    padding: 6px 10px;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .menu li.active {
    background: var(--accent);
    color: #fff;
  }
  .menu li[aria-selected="true"]:not(.active) {
    font-weight: 600;
  }
  .menu li.empty {
    cursor: default;
    color: var(--muted);
  }
</style>
